/**
 * "Add as new caddie" helpers for the Registration form's Caddie Name dropdown (IGFEMS-577).
 * Pure functions plus one DOM attach function, kept out of FormioTabRenderer.
 */

const EMAIL_RE = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

/** "Rory Valli" -> { name: "Rory", lname: "Valli" }; extra words go to the last name. */
export function splitTypedName(text) {
  const parts = String(text || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return { name: "", lname: "" };
  return { name: parts[0], lname: parts.slice(1).join(" ") };
}

/** Returns an error message, or "" when the form is valid. */
export function validateNewCaddie({ name, lname, email }) {
  if (!String(name || "").trim() || !String(lname || "").trim()) {
    return "First name and last name are required.";
  }
  if (!EMAIL_RE.test(String(email || "").trim())) {
    return "Enter a valid email address.";
  }
  return "";
}

/** Smallest valid caddie: person (name, email) + participant in the Caddie group. */
export function buildCreateCaddiePayload({ name, lname, email }, { competitionId, groupId }) {
  return {
    personData: {
      name: String(name).trim(),
      lname: String(lname).trim(),
      email: String(email).trim(),
    },
    participantData: { competition_id: competitionId, group_id: groupId },
    roles: [],
    relationships: [],
    sendSetPasswordEmail: false,
  };
}

/** Shape the dropdown expects (same as GET /personRoles/caddies rows). */
export function toCaddieOption(person) {
  const name = person?.name || "";
  const lname = person?.lname || "";
  return {
    _id: person._id,
    name,
    lname,
    email: String(person?.email || "").trim(),
    email2: "",
    label: `${name} ${lname}`.trim(),
  };
}

/** True once after a caddie is added: the renderer must not reset the form from saved data. */
export function takeSavedRestoreSkip(ref) {
  if (!ref?.current) return false;
  ref.current = false;
  return true;
}

const OPTION_CLASS = "add-caddie-option";

/**
 * Choices.js fires a bubbling `search` event with { value, resultCount }. When a search
 * finds nobody, add a button under the list. Returns a detach function.
 */
export function attachAddCaddieOption(container, { selectKey, onRequestAdd }) {
  if (!container || !selectKey) return () => {};

  const clear = (select) => {
    select?.closest?.(".choices")?.querySelectorAll(`.${OPTION_CLASS}`).forEach((n) => n.remove());
  };

  const onSearch = (e) => {
    const select = e.target;
    const comp = select?.closest?.(`.formio-component-${selectKey}`);
    const dropdown = select?.closest?.(".choices")?.querySelector(".choices__list--dropdown");
    if (!comp || !dropdown) return;
    clear(select);
    const text = String(e.detail?.value || "").trim();
    if (text.length < 2 || e.detail?.resultCount !== 0) return;

    const row = document.createElement("div");
    row.className = OPTION_CLASS;
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = `${OPTION_CLASS}__btn`;
    btn.textContent = `+ Add "${text}" as a new caddie`;
    btn.addEventListener("mousedown", (ev) => ev.preventDefault());
    btn.addEventListener("click", () => {
      const comps = Array.from(container.querySelectorAll(`.formio-component-${selectKey}`));
      onRequestAdd({ text, rowIndex: Math.max(0, comps.indexOf(comp)) });
      clear(select);
      if (document.activeElement && document.activeElement.blur) document.activeElement.blur();
    });
    row.appendChild(btn);
    dropdown.appendChild(row);
  };

  const onHide = (e) => clear(e.target);

  container.addEventListener("search", onSearch);
  container.addEventListener("hideDropdown", onHide);
  return () => {
    container.removeEventListener("search", onSearch);
    container.removeEventListener("hideDropdown", onHide);
  };
}
